import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * Data-only context for Infrastructure components.
 *
 * Provides table data, columns, and loading state without selection or filter state.
 * Components that only need data (e.g., table content rendering) can subscribe to this
 * context instead of the combined context to avoid unnecessary re-renders.
 *
 * **Separation of Concerns:**
 * - InfrastructureDataContext: data, columns, emptyText, isActioning
 *
 * This prevents cascade re-renders when unrelated state changes (e.g., selection changes
 * don't cause data consumers to re-render).
 */
const InfrastructureDataContext = createContext(null);

/**
 * Provider for infrastructure data context.
 *
 * @param {Object} props
 * @param {Array} props.data - Table data array
 * @param {Array} props.columns - Table column configuration
 * @param {string} [props.emptyText] - Text to display when table is empty
 * @param {boolean} [props.isActioning] - Whether an action is in progress
 * @param {React.ReactNode} props.children - Child components
 */
export function InfrastructureDataProvider({ data, columns, emptyText, isActioning, children }) {
    const value = useMemo(
        () => ({
            data,
            columns,
            emptyText,
            isActioning,
        }),
        [data, columns, emptyText, isActioning]
    );

    return <InfrastructureDataContext.Provider value={value}>{children}</InfrastructureDataContext.Provider>;
}

InfrastructureDataProvider.propTypes = {
    data: PropTypes.array.isRequired,
    columns: PropTypes.array.isRequired,
    emptyText: PropTypes.string,
    isActioning: PropTypes.bool,
    children: PropTypes.node,
};

InfrastructureDataProvider.defaultProps = {
    emptyText: '',
    isActioning: false,
    children: null,
};

/**
 * Hook to consume infrastructure data context.
 *
 * @returns {Object} Data context value
 * @throws {Error} If used outside of InfrastructureDataProvider
 */
export function useInfrastructureData() {
    const context = useContext(InfrastructureDataContext);
    if (!context) {
        throw new Error('useInfrastructureData must be used within InfrastructureDataProvider');
    }
    return context;
}
